import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Phone, Mail, MapPin } from 'lucide-react';
import { contactService } from '@/services/contactService';
import { CONTACT_INFO } from '@/utils/constants';

export default function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        loanType: 'Personal Loan',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await contactService.createContact(formData);
            toast.success('Thank you! We will contact you soon.');
            setFormData({
                name: '',
                email: '',
                phone: '',
                loanType: 'Personal Loan',
                message: ''
            });
        } catch (error) {
            toast.error('Failed to submit form. Please try again.');
        }
        setLoading(false);
    };

    return (
        <div className="contact-page" data-testid="contact-page">
            <div className="page-header">
                <h1>Contact Us</h1>
                <p>Get in touch with us for your loan requirements</p>
            </div>

            <div className="container">
                <div className="contact-grid">
                    <Card className="contact-form-card">
                        <CardHeader>
                            <CardTitle>Send us a message</CardTitle>
                            <CardDescription>Fill out the form below and we'll get back to you shortly</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="contact-form">
                                <div className="form-group">
                                    <Label>Full Name *</Label>
                                    <Input
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Enter your name"
                                        data-testid="contact-name-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label>Email *</Label>
                                    <Input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="Enter your email"
                                        data-testid="contact-email-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label>Phone *</Label>
                                    <Input
                                        required
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="Enter your phone"
                                        data-testid="contact-phone-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label>Loan Type *</Label>
                                    <select
                                        className="select-input"
                                        value={formData.loanType}
                                        onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                                        data-testid="contact-loantype-select"
                                    >
                                        <option>Personal Loan</option>
                                        <option>Home Loan</option>
                                        <option>Business Loan</option>
                                        <option>Education Loan</option>
                                        <option>Agriculture Loan</option>
                                        <option>Women Group Loan</option>
                                        <option>Property Loan</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <Label>Message</Label>
                                    <Textarea
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        placeholder="Tell us about your requirements"
                                        rows={4}
                                        data-testid="contact-message-input"
                                    />
                                </div>

                                <Button type="submit" className="w-full" disabled={loading} data-testid="contact-submit-btn">
                                    {loading ? 'Sending...' : 'Submit Application'}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <div className="contact-info">
                        <Card>
                            <CardHeader>
                                <CardTitle>Contact Information</CardTitle>
                            </CardHeader>
                            <CardContent className="contact-details">
                                <div className="contact-detail-item">
                                    <Phone className="icon" />
                                    <div>
                                        <h4>Phone</h4>
                                        <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`}>{CONTACT_INFO.phone}</a>
                                    </div>
                                </div>
                                <div className="contact-detail-item">
                                    <Mail className="icon" />
                                    <div>
                                        <h4>Email</h4>
                                        <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
                                    </div>
                                </div>
                                <div className="contact-detail-item">
                                    <MapPin className="icon" />
                                    <div>
                                        <h4>Address</h4>
                                        <p>{CONTACT_INFO.address}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};
